"use client";

/**
 * P0-03 · Client feature-flag context. Hydrated once from the RSC layer (the
 * active store's resolved flags), so client components read the same flags with
 * no extra round-trip. Client gating is UX only — the server enforces access.
 */
import { createContext, useContext } from "react";

import type { FeatureFlagKey, FeatureFlags } from "~/config/feature-flags";

const FeatureFlagsContext = createContext<FeatureFlags | null>(null);

export function FeatureFlagsProvider({
  flags,
  children,
}: {
  flags: FeatureFlags;
  children: React.ReactNode;
}) {
  return (
    <FeatureFlagsContext.Provider value={flags}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags(): FeatureFlags {
  const flags = useContext(FeatureFlagsContext);
  if (!flags) {
    throw new Error(
      "useFeatureFlags must be used within a FeatureFlagsProvider",
    );
  }
  return flags;
}

/** Whether a single feature is enabled for the active store (client-side). */
export function useFeatureFlag(key: FeatureFlagKey): boolean {
  return useFeatureFlags()[key];
}

/**
 * P17-10 · The same question, asked by a component that may legitimately
 * render outside the provider.
 *
 * The throwing version above is right for a component that only ever exists
 * inside the storefront tree: a missing provider there is a wiring bug and
 * should say so loudly. But a flag-gated *addition* to a shared presentational
 * component — the BNPL message under `PriceBlock`'s price — is rendered by
 * jsdom tests and by the kitchen sink with no provider at all, and "the flag
 * is off" is the correct reading of "there is no store here", not a crash on a
 * price the test was actually checking.
 *
 * Off, never on: absence can only ever close a door.
 */
export function useOptionalFeatureFlag(key: FeatureFlagKey): boolean {
  return useContext(FeatureFlagsContext)?.[key] ?? false;
}
